import { Dispatch, FunctionComponent, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileUploader } from "react-drag-drop-files";

import { faUpload } from "@fortawesome/pro-regular-svg-icons";

import { Icon, Modal } from "@/components";
import { TextArea } from "@/components/TextArea/TextArea";
import { getAllErrors, renderErrorMessages } from "@/utils";
import {
	useAddCompetitorsMutation,
	useUploadCompetitorsMutation,
} from "@/pages/Private/redux/organization/organization.api";

import { CompetitorAddResult } from "../../Contacts/schema/contacts";

interface AddCompetitorsModalProps {
	isOpen: boolean;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
}

/** Splits the pasted block into entries — one per line, commas and semicolons also separate. */
export const splitCompetitorEntries = (value: string): string[] =>
	value
		.split(/[\n,;]/)
		.map((entry) => entry.trim())
		.filter(Boolean);

/**
 * "Add competitors" (PREC-3594): paste company domains and/or LinkedIn company URLs.
 *
 * Entries that match a company in the account get the full competitor treatment; the rest are
 * blocked on their identifier alone and show up on the Blacklist tab — which is what the result
 * summary says, so "added nothing" and "added everything" never look alike.
 */
export const AddCompetitorsModal: FunctionComponent<AddCompetitorsModalProps> = ({
	isOpen,
	setIsOpen,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`contacts.competitors.${key}`);

	const [value, setValue] = useState("");
	const [file, setFile] = useState<File | null>(null);
	const [result, setResult] = useState<CompetitorAddResult | null>(null);
	const [addCompetitors, { isLoading: isAdding, error: addError }] = useAddCompetitorsMutation();
	const [uploadCompetitors, { isLoading: isUploading, error: uploadError }] =
		useUploadCompetitorsMutation();

	const entries = splitCompetitorEntries(value);

	/** A picked file wins: the user chose the import over whatever is left in the textarea. */
	const onSubmit = async () => {
		try {
			if (file) {
				const formData = new FormData();

				formData.append("file", file, file.name);

				setResult(await uploadCompetitors(formData).unwrap());
				setFile(null);

				return;
			}

			if (entries.length === 0) {
				return;
			}

			setResult(await addCompetitors({ entries }).unwrap());
			setValue("");
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<Modal
			handleClose={() => setIsOpen(false)}
			handleSave={onSubmit}
			isLoading={isAdding || isUploading}
			isOpened={isOpen}
			submitButtonText={ts("addSubmit")}
			title={ts("addTitle")}
		>
			<div className="flex flex-col gap-4">
				<p className="text-xs text-text-meta">{ts("addHint")}</p>

				<TextArea
					handleChange={(event) => setValue(event.target.value)}
					label={ts("addLabel")}
					name="entries"
					placeholder={"acme.com\nhttps://www.linkedin.com/company/acme"}
					showError={false}
					value={value}
				/>

				<FileUploader
					handleChange={(picked: File | File[]) =>
						setFile(Array.isArray(picked) ? picked[0] : picked)
					}
					name="file"
					types={["csv"]}
				>
					<div className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dotted border-border-light p-6 text-center">
						<Icon className="h-5 w-5 text-text-meta" icon={faUpload} />
						<span className="text-sm text-text-meta">{file?.name || ts("addUploadHint")}</span>
					</div>
				</FileUploader>

				{result ? (
					<div className="flex flex-col gap-1 text-xs text-text-meta">
						<span>
							{t("contacts.competitors.addSummary", {
								matched: result.matched,
								blocked: result.blockedWithoutCompany,
								duplicates: result.duplicates,
							})}
						</span>

						{result.invalid.length ? (
							<span className="text-status-error">
								{t("contacts.competitors.addInvalid", {
									entries: result.invalid.join(", "),
								})}
							</span>
						) : null}
					</div>
				) : null}

				{getAllErrors(addError ?? uploadError, []).length
					? renderErrorMessages(getAllErrors(addError ?? uploadError, []))
					: null}
			</div>
		</Modal>
	);
};
